import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import React, { useRef, useState } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import classnames from 'classnames';
import { Reorder, useDragControls, useMotionValue } from 'framer-motion';
import isEqual from 'lodash/isEqual';
import { Flex, Body as Body$1, Input, Checkbox, SegmentedControl, Button, Radio } from '@cognite/cogs-core';
import { SearchIcon, ChevronLeftIcon, CloseIcon, DragHandleVerticalIcon, CheckmarkToggleIcon, ChevronRightSmallIcon } from '@cognite/cogs-icons';
import { useSlots, IconWrapper } from '@cognite/cogs-utils';
import { focusLastItemInSection, focusFirstItemInSection, focusNextItem as _focusNextItem, focusPrevItem as _focusPrevItem } from './focus-utils.js';
import { useSelectPanelContext } from './select-panel-context.js';

var Trigger = function Trigger(_ref) {
  var children = _ref.children;
  return children;
};
var Header = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var title = _a.title,
    description = _a.description,
    children = _a.children,
    className = _a.className,
    props = __rest(_a, ["title", "description", "children", "className"]);
  var _useSlots = useSlots(children, {
      backButton: BackButton,
      closeButton: CloseButton,
      searchBar: SearchBar,
      segmentedControl: SelectPanelSegmentedControl,
      selectAll: SelectAll
    }),
    _useSlots2 = _slicedToArray(_useSlots, 2),
    slots = _useSlots2[0],
    restChildren = _useSlots2[1];
  return jsxs(Flex, Object.assign({
    ref: ref,
    direction: "column",
    gap: 12,
    className: classnames('cogs-lab', 'cogs-lab-select-panel__header-wrapper', className)
  }, props, {
    children: [jsxs(Flex, {
      gap: 8,
      alignItems: "flex-start",
      children: [slots.backButton && jsx(Flex, {
        className: classnames('cogs-lab', 'cogs-lab-select-panel__header-wrapper__back-icon'),
        children: slots.backButton
      }), jsxs(Flex, {
        className: classnames('cogs-lab', 'cogs-lab-select-panel__header-wrapper__text-container'),
        direction: "column",
        children: [jsx(Body$1, {
          size: "medium",
          strong: true,
          children: title
        }), jsx(Body$1, {
          size: "small",
          muted: true,
          children: description
        })]
      }), slots.closeButton && jsx(Flex, {
        className: classnames('cogs-lab', 'cogs-lab-select-panel__header-wrapper__close-icon'),
        children: slots.closeButton
      })]
    }), slots.searchBar, slots.segmentedControl, slots.selectAll, restChildren]
  }));
});
var Footer = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var children = _a.children,
    className = _a.className,
    props = __rest(_a, ["children", "className"]);
  return jsx(Flex, Object.assign({
    ref: ref,
    justifyContent: "space-between",
    className: classnames('cogs-lab', 'cogs-lab-select-panel__footer-wrapper', className)
  }, props, {
    children: children
  }));
});
var Body = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var children = _a.children,
    className = _a.className,
    _a$length = _a.length,
    length = _a$length === undefined ? 'medium' : _a$length,
    props = __rest(_a, ["children", "className", "length"]);
  return jsx("div", Object.assign({
    ref: ref,
    className: classnames(className, 'cogs-lab', 'cogs-lab-select-panel__body-wrapper', "cogs-lab-select-panel__body-wrapper--length-".concat(length))
  }, props, {
    children: children
  }));
});
var SearchBar = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var className = _a.className,
    props = __rest(_a, ["className"]);
  return jsx(Input, Object.assign({
    ref: ref,
    size: "small",
    fullWidth: true,
    className: classnames(className, 'cogs-lab', 'cogs-lab-select-panel__search-bar')
  }, props, {
    placeholder: "Search",
    icon: jsx(SearchIcon, {})
  }));
});
var SelectAll = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var className = _a.className,
    props = __rest(_a, ["className"]);
  return jsx(Checkbox, Object.assign({
    ref: ref,
    className: classnames(className, 'cogs-lab', 'cogs-lab-select-panel__select-all')
  }, props));
});
// SegmentedControl doesn't take ref as a prop, so no need to use forwardRef here
var SelectPanelSegmentedControl = function SelectPanelSegmentedControl(_a) {
  var className = _a.className,
    props = __rest(_a, ["className"]);
  return jsx(SegmentedControl, Object.assign({
    size: "small",
    className: classnames(className, 'cogs-lab', 'cogs-lab-select-panel__segmented-control')
  }, props, {
    fullWidth: true
  }));
};
var BackButton = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return jsx(Button, Object.assign({
    ref: ref,
    "aria-label": "Back button"
  }, props, {
    type: "ghost",
    size: "small",
    icon: jsx(ChevronLeftIcon, {})
  }));
});
var CloseButton = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _useSelectPanelContex = useSelectPanelContext(),
    setIsVisible = _useSelectPanelContex.setIsVisible;
  return jsx(Button, Object.assign({
    ref: ref,
    "aria-label": "Close button"
  }, props, {
    onClick: function onClick(e) {
      var _a;
      setIsVisible === null || setIsVisible === undefined ? undefined : setIsVisible(false);
      (_a = props.onClick) === null || _a === undefined ? undefined : _a.call(props, e);
    },
    type: "ghost",
    size: "small",
    icon: jsx(CloseIcon, {})
  }));
});
var FooterButton = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _useSelectPanelContex2 = useSelectPanelContext(),
    setIsVisible = _useSelectPanelContex2.setIsVisible;
  return jsx(Button, Object.assign({
    ref: ref
  }, props, {
    onClick: function onClick(e) {
      var _a;
      setIsVisible === null || setIsVisible === undefined ? undefined : setIsVisible(false);
      (_a = props.onClick) === null || _a === undefined ? undefined : _a.call(props, e);
    }
  }));
});
// We need this generic, so that the end user can get intellisense in onReorder
// callback
var Section = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var children = _a.children,
    title = _a.title,
    _a$items = _a.items,
    items = _a$items === undefined ? [] : _a$items,
    onReorder = _a.onReorder,
    className = _a.className,
    props = __rest(_a, ["children", "title", "items", "onReorder", "className"]);
  var handleOnReorder = function handleOnReorder() {
    var newItems = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    // In the search use case if the user decides to not render items
    // that don't match the search string, the framer-motion returns
    // only the list of items that were rendered which replaces the
    // original list causing the other items to not show up even after
    // clearing the search string. This function uses swap based
    // reordering such that after removing the search filter, all the
    // other items stay in place and only the order of searched items
    // change among themselves without affecting the other items.
    var computeNewOrderedItems = function computeNewOrderedItems() {
      if (newItems.length === 1) return items;
      if (newItems.length >= items.length) return newItems;
      var findIndexInSourceItems = function findIndexInSourceItems(newItem) {
        return items.findIndex(function (item) {
          return isEqual(item, newItem);
        });
      };
      for (var i = 1; i < newItems.length; i++) {
        var firstItemIndex = findIndexInSourceItems(newItems[i - 1]);
        var secondItemIndex = findIndexInSourceItems(newItems[i]);
        if (firstItemIndex < secondItemIndex) continue;
        items[firstItemIndex] = newItems[i];
        items[secondItemIndex] = newItems[i - 1];
      }
      return items;
    };
    var newOrderedItems = computeNewOrderedItems();
    onReorder === null || onReorder === undefined ? undefined : onReorder(newOrderedItems.filter(function (item) {
      return !!item;
    }));
  };
  return jsxs(Flex, Object.assign({
    ref: ref,
    direction: "column",
    className: classnames('cogs-lab', 'cogs-lab-select-panel__section', className)
  }, props, {
    children: [title && jsx(Body$1, {
      muted: true,
      size: "x-small",
      children: title
    }), jsx(Reorder.Group, {
      as: "ul",
      className: classnames('cogs-lab', 'cogs-lab-select-panel__section__menu-wrapper'),
      axis: "y",
      layoutScroll: true,
      values: items,
      onReorder: handleOnReorder,
      role: "menu",
      children: children
    })]
  }));
});
// We need this generic, so that the end user can get intellisense in onReorder
// callback in the Section component
var Item = /*#__PURE__*/React.forwardRef(function (_a, outerRef) {
  var label = _a.label,
    description = _a.description,
    variant = _a.variant,
    checked = _a.checked,
    indeterminate = _a.indeterminate,
    disabled = _a.disabled;
    _a.onKeyDown;
    var onClick = _a.onClick,
    trailingContent = _a.trailingContent,
    _a$size = _a.size,
    size = _a$size === undefined ? 'small' : _a$size,
    reorderable = _a.reorderable,
    item = _a.item,
    className = _a.className,
    style = _a.style,
    onActiveChange = _a.onActiveChange,
    props = __rest(_a, ["label", "description", "variant", "checked", "indeterminate", "disabled", "onKeyDown", "onClick", "trailingContent", "size", "reorderable", "item", "className", "style", "onActiveChange"]);
  var innerRef = useRef(null);
  var combinedRefs = useMergeRefs([innerRef, outerRef]);
  var dragControls = useDragControls();
  var x = useMotionValue(0);
  var y = useMotionValue(0);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isDragging = _useState2[0],
    setIsDragging = _useState2[1];
  var extendedWrapperClassname = classnames(className, {
    'cogs-lab': true,
    'cogs-lab-select-panel__menu-item': true,
    'cogs-lab-select-panel__menu-item--small': size === 'small',
    'cogs-lab-select-panel__menu-item--medium': size === 'medium',
    'cogs-lab-select-panel__menu-item--toggled': !disabled && variant === 'toggle' && checked,
    'cogs-lab-select-panel__menu-item--checked': !disabled && variant && ['checkbox', 'radio'].includes(variant) && (!!checked || !!indeterminate),
    'cogs-lab-select-panel__menu-item--disabled': disabled,
    'cogs-lab-select-panel__menu-item--is-dragging': isDragging
  });
  var getRole = function getRole() {
    if (variant === 'checkbox' || variant === 'toggle') return 'menuitemcheckbox';
    if (variant === 'radio') return 'menuitemradio';
    return 'menuitem';
  };
  var handleClick = function handleClick(e) {
    // since when the user ends dragging, a mouseUp event is fired, which
    // can be viewed as onClick and we want to prevent that from happening,
    // that's why we return early if these handlers were called as a result
    // of dragEnd event
    if (isDragging || disabled) return;
    checked !== undefined && variant && ['checkbox', 'toggle', 'radio'].includes(variant) && (onActiveChange === null || onActiveChange === undefined ? undefined : onActiveChange(!checked));
    onClick === null || onClick === undefined ? undefined : onClick(e);
  };
  var handleKeyUp = function handleKeyUp(e) {
    var _a;
    if (isDragging || disabled) return;
    switch (e.code) {
      case 'Space':
        e.preventDefault();
        e.currentTarget.click();
        break;
      case 'ArrowUp':
        _focusPrevItem(e.currentTarget);
        break;
      case 'ArrowDown':
        _focusNextItem(e.currentTarget);
        break;
      case 'PageUp':
        e.preventDefault();
        focusFirstItemInSection(e.currentTarget);
        break;
      case 'PageDown':
        e.preventDefault();
        focusLastItemInSection(e.currentTarget);
        break;
      case 'Home':
        e.preventDefault();
        focusFirstItemInSection(e.currentTarget);
        break;
      case 'End':
        e.preventDefault();
        focusLastItemInSection(e.currentTarget);
        break;
    }
    (_a = props.onKeyUp) === null || _a === undefined ? undefined : _a.call(props, e);
  };
  return jsxs(Reorder.Item, Object.assign({
    ref: combinedRefs,
    as: "li",
    className: extendedWrapperClassname,
    style: Object.assign({
      x: x,
      y: y
    }, style),
    role: getRole(),
    tabIndex: !disabled ? 0 : -1,
    "data-testid": 'select-panel-item'
  }, props, {
    "aria-disabled": disabled,
    "aria-checked": checked,
    "data-variant": variant,
    value: item,
    layout: "position",
    onClick: handleClick,
    onKeyUp: handleKeyUp,
    dragListener: false,
    dragControls: dragControls,
    onDragStart: function onDragStart() {
      return setIsDragging(true);
    },
    // this is required to prevent horizontal misalignment in a scrollable
    // container
    onDrag: function onDrag() {
      return x.set(0);
    },
    onDragEnd: function onDragEnd() {
      return setIsDragging(false);
    },
    children: [reorderable && jsx(Flex, {
      className: classnames('cogs-lab', 'cogs-lab-select-panel__menu-item__drag-icon-container'),
      children: jsx(IconWrapper, {
        size: 16,
        role: "button",
        onPointerDown: function onPointerDown(e) {
          return !disabled && dragControls.start(e);
        },
        children: jsx(DragHandleVerticalIcon, {})
      })
    }), jsxs(Flex, {
      className: classnames('cogs-lab', 'cogs-lab-select-panel__menu-item__left-elem-container'),
      children: [variant === 'toggle' && checked && jsx(IconWrapper, {
        size: 16,
        children: jsx(CheckmarkToggleIcon, {})
      }), variant === 'checkbox' && jsx(Checkbox, {
        checked: !!checked || !!indeterminate,
        indeterminate: indeterminate,
        disabled: disabled
      }), variant === 'radio' && jsx(Radio, {
        name: label !== null && label !== undefined ? label : '',
        checked: checked,
        disabled: disabled
      })]
    }), jsx(Body$1, {
      size: "medium",
      className: classnames('cogs-lab', 'cogs-lab-select-panel__menu-item__label'),
      children: label
    }), variant !== 'submenu' && description && jsx(Body$1, {
      muted: true,
      size: "x-small",
      className: classnames('cogs-lab', 'cogs-lab-select-panel__menu-item__description'),
      children: description
    }), jsxs(Flex, {
      className: classnames('cogs-lab', 'cogs-lab-select-panel__menu-item__right-elem-container'),
      children: [trailingContent, variant === 'submenu' && jsx(IconWrapper, {
        size: 16,
        children: jsx(ChevronRightSmallIcon, {})
      })]
    })]
  }));
});

export { BackButton, Body, CloseButton, Footer, FooterButton, Header, Item, SearchBar, Section, SelectAll, SelectPanelSegmentedControl, Trigger };
